import { getAsset, getFlight, listAssetsByFlight } from "./store";
import { OFFLINE_BASEMAP_PROVIDER } from "../map/basemaps";

export type AuditStatus =
  | "ok"
  | "evicted"
  | "not-cached"
  | "schema-mismatch"
  | "partial";

export interface AuditResult {
  status: AuditStatus;
  missing: string[];
}

const CURRENT_SCHEMA = 1;

export async function auditFlight(flightId: string): Promise<AuditResult> {
  const record = await getFlight(flightId);
  if (record === null) return { status: "not-cached", missing: [] };
  // A cancelled or failed download: the per-asset checks below would report a
  // long, useless `missing` list. The only useful action is Remove.
  if (record.partial === true) return { status: "partial", missing: [] };
  if (record.schemaVersion !== CURRENT_SCHEMA) {
    return { status: "schema-mismatch", missing: [] };
  }

  const missing: string[] = [];
  for (const kind of ["video", "track", "meta", "thumbnail", "annotations-baseline"]) {
    if ((await getAsset(`${flightId}:${kind}`)) === null) missing.push(kind);
  }
  if ((record.assetCounts.preview ?? 0) === 1 && (await getAsset(`${flightId}:preview`)) === null) {
    missing.push("preview");
  }

  const assets = await listAssetsByFlight(flightId);
  const tiles = assets.filter((a) => a.kind === "tile").length;
  const panos = assets.filter((a) => a.kind === "pano").length;
  const frames = assets.filter((a) => a.kind === "frame-full").length;
  if (tiles !== record.assetCounts.tiles) {
    missing.push(`tiles (${tiles}/${record.assetCounts.tiles})`);
  }
  if (panos !== record.assetCounts.panoramas) {
    missing.push(`panoramas (${panos}/${record.assetCounts.panoramas})`);
  }
  if (frames !== record.assetCounts.frames) {
    missing.push(`frames (${frames}/${record.assetCounts.frames})`);
  }
  // Plan 13 counts are optional: records written before flight sets carry
  // neither field, and must not be reported as evicted for lacking them.
  const overlayCount = assets.filter((a) => a.kind === "overlay").length;
  if (
    record.assetCounts.overlays !== undefined &&
    overlayCount !== record.assetCounts.overlays
  ) {
    missing.push(`overlays (${overlayCount}/${record.assetCounts.overlays})`);
  }
  const setTrackCount = assets.filter((a) => a.kind === "set-track").length;
  if (
    record.assetCounts.setTracks !== undefined &&
    setTrackCount !== record.assetCounts.setTracks
  ) {
    missing.push(`set tracks (${setTrackCount}/${record.assetCounts.setTracks})`);
  }
  // The tiles are all there, but from a retired provider (records written
  // before 2026-09-09 hold CARTO + keyless Esri tiles). Refresh replaces them.
  if (record.basemapProvider !== OFFLINE_BASEMAP_PROVIDER) {
    missing.push("basemaps (older provider — Refresh to replace)");
  }

  return missing.length === 0
    ? { status: "ok", missing: [] }
    : { status: "evicted", missing };
}
